import { CommonModule } from '@angular/common';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, computed, HostBinding, inject, input, OnInit, ViewEncapsulation } from '@angular/core';
import { Route, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { NavigationEnd } from '@angular/router';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { AuthorizeGuard } from '@jms/tenant/module.jms.authorisation';
import { ApplicationActions, ApplicationState } from '@jms/tenant/module.jms.state';
import { FeatureFlags, TenantSubscription } from '@jms/model';
import { UploaderModule } from '@jms/ui/ui.uploader';
import { HelpNavComponent } from '../help.nav/help.nav.component';
import { UserNavComponent } from '../user.nav/user.nav.component';
import { InboxBadgeService } from '../sidenav/inbox.badge.service';
import { InlineSidenavGroupComponent } from './inline.sidenav.group.component';

@Component({
    selector: 'inline-side-nav',
    templateUrl: 'inline.sidenav.component.html',
    styleUrls: ['inline.sidenav.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, CdkScrollable, CdkAccordionModule, RouterLink, RouterLinkActive, InlineSidenavGroupComponent, UploaderModule, HelpNavComponent, UserNavComponent]
})
export class InlineSideNavComponent implements OnInit {

    protected readonly inboxBadgeService = inject(InboxBadgeService);
    private readonly router = inject(Router);
    private readonly authorizeGuard = inject(AuthorizeGuard);
    public readonly actions = inject(ApplicationActions);

    public readonly $toggle = input<boolean>(false, { alias: 'toggle' });
    public readonly $state = input<ApplicationState>(undefined, { alias: 'state' });

    @HostBinding('class.expanded') protected get hostExpanded() { return this.$toggle(); }
    @HostBinding('class.nav-dev') protected get isDev() { return !ConfigService?.environment?.production; }

    public readonly signingEndpoint = '/api/attachments/sas';
    public readonly successEndpoint = '/api/attachments/success';

    public readonly accountUrl: string;
    public readonly buildVersion: string;
    public readonly plan: TenantSubscription | 'Pro';

    private readonly $navigationEnd = toSignal(
        this.router.events.pipe(filter(e => e instanceof NavigationEnd)),
        { initialValue: null }
    );

    protected readonly $config = computed<Route[]>(() => {

        this.$navigationEnd();
        const state = this.$state();
        return this.buildConfig(state);
    });

    protected readonly $primaryRoutes = computed(() =>
        this.$config().filter(x => !this.isPinnedPath(x.path))
    );
    protected readonly $inboxRoute = computed(() => this.$config().find(x => x.path === 'inbox'));
    protected readonly $aiAssistantRoute = computed(() => {
        const enabled = ConfigService.environment.featureFlags?.[FeatureFlags.AIAssistant] ?? false;
        return enabled ? this.$config().find(x => x.path === 'ai-assistant') : undefined;
    });
    protected readonly $agentRoute = computed(() => {
        const enabled = ConfigService.environment.featureFlags?.[FeatureFlags.AIAssistant] ?? false;
        return enabled ? this.$config().find(x => x.path === 'agent') : undefined;
    });
    protected readonly $settingsRoute = computed(() => this.$config().find(x => x.path === 'settings'));

    constructor() {

        const env = ConfigService.environment;
        const planSub = env?.plan?.subscription;
        this.plan = planSub === TenantSubscription.Professional ? 'Pro' : planSub;
        this.buildVersion = env?.build === 'undefined-undefined' ? 'Dev Version' : env?.build;
        this.accountUrl = `${env?.jms?.loginHref}/account`;
    }

    ngOnInit(): void {

        if (this.$inboxRoute()) this.inboxBadgeService.loadUnreadCount();
    }

    protected onLogout(): void {

        if (window.Intercom) {
            window.Intercom('shutdown');
        }
        this.actions.logout(true);
    }

    protected onPreventTabKey(e: Event): void {

        e.preventDefault();
    }

    protected hasChildren(route: Route): boolean {

        return (route.children?.length ?? 0) > 0;
    }

    private isPinnedPath(path: string): boolean {

        return path === 'settings' || path === 'inbox' || path === 'ai-assistant' || path === 'agent';
    }

    private buildConfig(state: ApplicationState): Route[] {

        const children = this.router.config[0]?.children ?? [];

        return children
            .filter(x => !x.redirectTo)
            .filter(x => this.authorizeGuard.canLoadPath(x.path))
            .filter(x => this.passesRestrictions(x, state))
            .map(x => this.collapseSingleChild(x, state));
    }

    private passesRestrictions(route: Route, state: ApplicationState): boolean {

        if (route?.data?.hidden) return false;
        if (!state) return false;
        const restrictions = route.data?.restrictions as string[];
        return restrictions != null && restrictions.indexOf(state.info.tenantStatus) >= 0;
    }

    private collapseSingleChild(route: Route, state: ApplicationState): Route {

        if (!route?.children?.length) return route;

        const filteredChildren = route.children
            .filter(child => this.authorizeGuard.canLoadPath(`/${route.path}/${child.path}`))
            .filter(child => this.passesRestrictions(child, state));

        if (filteredChildren.length === 1) {
            const child = filteredChildren[0];
            return { ...child, path: `${route.path}/${child.path}` };
        }

        return { ...route, children: filteredChildren };
    }
}
